import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { API, useAuth } from "../App";
import axios, { setActiveOrgId, initializeOrganization } from "../lib/api";
import { toast } from "sonner";
import { Users, CheckCircle, XCircle, Clock, ArrowRight, LogIn } from "lucide-react";

export default function InviteAcceptPage() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const { user } = useAuth();

  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accepting, setAccepting] = useState(false);
  const [accepted, setAccepted] = useState(false);

  // Auto-redirect unauthenticated users immediately to login (before fetching invite info)
  useEffect(() => {
    if (!token) return; // handled in second effect
    const hasToken = !!localStorage.getItem("auth_token");
    if (!hasToken) {
      // Not authenticated (no valid token) — redirect to login with invite context
      navigate(`/login?invite=1&token=${encodeURIComponent(token)}`, { replace: true });
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) {
      setError("Enlace de invitación inválido. Asegúrate de usar el enlace completo del email.");
      setLoading(false);
      return;
    }
    // Only fetch info if authenticated (has a valid token)
    const hasToken = !!localStorage.getItem("auth_token");
    if (!hasToken) return; // first useEffect handles redirect

    axios.get(`${API}/org/users/invite/info?token=${token}`)
      .then(r => setInfo(r.data))
      .catch(err => {
        const msg = err.response?.data?.detail || "Invitación no encontrada o expirada";
        setError(msg);
      })
      .finally(() => setLoading(false));
  }, [token, user]);

  const handleAccept = async () => {
    const authToken = localStorage.getItem("auth_token");
    if (!authToken) {
      // Not authenticated — redirect to login with invite context
      navigate(`/login?invite=1&token=${encodeURIComponent(token)}`);
      return;
    }
    setAccepting(true);
    try {
      const res = await axios.post(
        `${API}/org/users/invite/accept`,
        { token },
        { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} }
      );
      // Switch active org to the newly joined org
      const joinedOrgId = res.data?.organization_id;
      const uid = user?.user_id || localStorage.getItem("active_user_id");
      if (joinedOrgId) {
        setActiveOrgId(joinedOrgId, uid);
        await initializeOrganization(uid);
      }
      setAccepted(true);
      toast.success(`Te has unido a ${info?.organization_name} correctamente`);
      // Reload organization context and redirect
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo aceptar la invitación");
    } finally {
      setAccepting(false);
    }
  };

  const ROLE_LABELS = { owner: "Administrador", member: "Miembro" };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#C8192C]" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAF9F7] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="text-2xl font-bold text-[#1A1A1A]">ProfitSystem</Link>
          <p className="text-sm text-slate-500 mt-1">Analytics & Profit Intelligence</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-[#E8E8E4] p-8">
          {accepted ? (
            <div className="text-center" data-testid="invite-accepted-success">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">¡Bienvenido al equipo!</h2>
              <p className="text-slate-500 text-sm">
                Te has unido a <strong>{info?.organization_name}</strong>. Redirigiendo al dashboard...
              </p>
            </div>
          ) : error ? (
            <div className="text-center" data-testid="invite-error">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-8 h-8 text-red-500" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 mb-2">Invitación no válida</h2>
              <p className="text-slate-500 text-sm mb-6">{error}</p>
              <Link to="/">
                <Button variant="outline" className="w-full">Volver al inicio</Button>
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-center mb-6">
                <div className="w-14 h-14 bg-[#C8192C]/10 rounded-2xl flex items-center justify-center">
                  <Users className="w-7 h-7 text-[#C8192C]" />
                </div>
              </div>

              <h1 className="text-xl font-bold text-center text-slate-900 mb-1" data-testid="invite-org-name">
                {info?.inviter_name} te ha invitado a unirte a
              </h1>
              <p className="text-2xl font-bold text-center text-[#C8192C] mb-6">{info?.organization_name}</p>

              <div className="bg-[#FAF9F7] rounded-xl p-4 mb-6 space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-500">Tu rol</span>
                  <span className="font-medium text-slate-900">{ROLE_LABELS[info?.role] || info?.role}</span>
                </div>
                {info?.email && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Invitado como</span>
                    <span className="font-medium text-slate-900">{info.email}</span>
                  </div>
                )}
                {info?.expires_at && (
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Válida hasta</span>
                    <span className="font-medium text-slate-900 flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(info.expires_at).toLocaleDateString("es-ES")}
                    </span>
                  </div>
                )}
              </div>

              {user ? (
                <Button
                  className="w-full bg-[#C8192C] hover:bg-[#a81424] text-white font-semibold h-11"
                  onClick={handleAccept}
                  disabled={accepting}
                  data-testid="accept-invite-btn"
                >
                  {accepting ? "Uniéndote..." : <>Aceptar invitación <ArrowRight className="ml-2 w-4 h-4" /></>}
                </Button>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm text-slate-500 text-center mb-4">
                    Necesitas una cuenta para aceptar esta invitación
                  </p>
                  <Link to={`/login?invite=1&token=${token}`}>
                    <Button className="w-full bg-[#C8192C] hover:bg-[#a81424] text-white font-semibold h-11" data-testid="invite-login-btn">
                      <LogIn className="mr-2 w-4 h-4" /> Iniciar sesión
                    </Button>
                  </Link>
                  <Link to={`/register?invite=1&token=${token}`}>
                    <Button variant="outline" className="w-full h-11" data-testid="invite-register-btn">
                      Crear cuenta nueva
                    </Button>
                  </Link>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
